import { useEffect, useState } from 'react';
import {
  folderInTrash,
  folderPath,
  isSealed,
  MIN_PASSWORD_LENGTH,
  placeMap,
  type MapDocument,
} from '@orbit-mind/domain';
import { buildScene } from '@orbit-mind/map-engine';
import { type useLibraryOrganization, useLocale } from '@orbit-mind/engine-react';
import type { MapSummary } from '@orbit-mind/offline-engine';
import { Button } from '../ui/controls';
import { Modal } from '../ui/Modal';
import { AdvancedExport } from '../editor/AdvancedExport';
import { textMeasurer } from '../canvas/measurer';
import { protectMap, unprotectMap } from '../engine/protection';
import type { libraryActions } from './organization';

export function MapActions({
  summary,
  organization,
  actions,
  onClose,
  onRefresh,
}: {
  summary: MapSummary;
  organization: ReturnType<typeof useLibraryOrganization>;
  actions: ReturnType<typeof libraryActions>;
  onClose: () => void;
  onRefresh: () => void;
}): React.JSX.Element {
  const { text: t } = useLocale();
  const [title, setTitle] = useState(summary.map.title);
  const [folder, setFolder] = useState(
    organization.library.placements[summary.map.id]?.folderId ?? '',
  );
  const [tags, setTags] = useState(
    organization.library.placements[summary.map.id]?.tags.join(', ') ?? '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [exported, setExported] = useState<MapDocument | null>(null);
  /** Whether the map is protected, once its document has been read. */
  const [sealed, setSealed] = useState<boolean | null>(null);
  const [protecting, setProtecting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [hint, setHint] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    let active = true;
    void actions
      .read(summary.map.id)
      .then((document) => {
        if (active) setSealed(isSealed(document));
      })
      .catch(() => {
        if (active) setSealed(null);
      });
    return () => {
      active = false;
    };
  }, [actions, summary.map.id]);
  const passwordProblem =
    sealed === true
      ? password === ''
        ? t('Type the password.', 'Digite a senha.')
        : null
      : password.length < MIN_PASSWORD_LENGTH
        ? t(
            `Use at least ${String(MIN_PASSWORD_LENGTH)} characters.`,
            `Use pelo menos ${String(MIN_PASSWORD_LENGTH)} caracteres.`,
          )
        : password !== confirm
          ? t('The passwords do not match.', 'As senhas não conferem.')
          : null;
  async function run(operation: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError('');
    try {
      await operation();
      onRefresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível concluir.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Modal
        open={exported === null}
        onOpenChange={(value) => {
          if (!value && !busy) onClose();
        }}
        title={t('Manage map', 'Gerenciar mapa')}
        size="narrow"
      >
        <form
          className="space-y-4 overflow-y-auto p-5"
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              if (title.trim() !== summary.map.title) await actions.rename(summary.map.id, title);
              const saved = await organization.update((previous) =>
                placeMap(previous, summary.map.id, folder || null, tags),
              );
              if (saved) onClose();
            });
          }}
        >
          <label className="block">
            {t('Title', 'Título')}
            <input
              required
              maxLength={200}
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
              }}
              className="border-edge bg-canvas mt-1 w-full rounded-lg border p-3"
            />
          </label>
          <label className="block">
            {t('Move to folder', 'Mover para pasta')}
            <select
              value={folder}
              onChange={(event) => {
                setFolder(event.target.value);
              }}
              className="border-edge bg-canvas mt-1 w-full rounded-lg border p-3"
            >
              <option value="">Home</option>
              {organization.library.folders
                .filter((f) => !folderInTrash(organization.library, f.id))
                .map((f) => (
                  <option key={f.id} value={f.id}>
                    {folderPath(organization.library, f.id)
                      .map((p) => p.name)
                      .join(' / ')}
                  </option>
                ))}
            </select>
          </label>
          <label className="block">
            Tags
            <input
              value={tags}
              onChange={(event) => {
                setTags(event.target.value);
              }}
              placeholder="estudo, projeto"
              className="border-edge bg-canvas mt-1 w-full rounded-lg border p-3"
            />
            <span className="text-ink-muted text-xs">
              {t('Separate with commas', 'Separe por vírgulas')}
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={busy}
              onClick={() => {
                void run(() => actions.backup(summary.map.id));
              }}
            >
              {t('Backup with attachments', 'Backup com anexos')}
            </Button>
            <Button type="submit" tone="primary" disabled={busy || !organization.ready}>
              {t('Save', 'Salvar')}
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                void run(async () => {
                  const id = await actions.duplicate(summary.map.id);
                  await organization.update((previous) =>
                    placeMap(previous, id, folder || null, tags),
                  );
                  onClose();
                });
              }}
            >
              {t('Duplicate', 'Duplicar')}
            </Button>
            <Button
              disabled={busy || sealed === true}
              onClick={() => {
                void run(async () => {
                  setExported(await actions.read(summary.map.id));
                });
              }}
            >
              {t('Export', 'Exportar')}
            </Button>
            {sealed !== null && !protecting && (
              <Button
                disabled={busy}
                data-testid={sealed ? 'unprotect-map' : 'protect-map'}
                onClick={() => {
                  setProtecting(true);
                  setPassword('');
                  setConfirm('');
                }}
              >
                {sealed
                  ? t('Remove password protection', 'Remover proteção por senha')
                  : t('Protect with a password', 'Proteger com senha')}
              </Button>
            )}
          </div>
          {protecting && sealed !== null && (
            <div className="bg-surface-sunken space-y-3 rounded-xl p-4" data-testid="protect-form">
              <p className="text-ink-muted text-sm">
                {sealed
                  ? t(
                      'A plain copy of the map is made, and the protected one goes to the Trash.',
                      'Uma cópia sem proteção é criada, e a protegida vai para a Lixeira.',
                    )
                  : t(
                      'The map is encrypted in this browser before it is saved or synced. A forgotten password cannot be recovered — not even by us. The unprotected original goes to the Trash; empty it to delete it for good.',
                      'O mapa é criptografado neste navegador antes de ser salvo ou sincronizado. Uma senha esquecida não pode ser recuperada — nem por nós. O original sem proteção vai para a Lixeira; esvazie-a para apagá-lo de vez.',
                    )}
              </p>
              <input
                type="password"
                aria-label={t('Password', 'Senha')}
                autoComplete={sealed ? 'current-password' : 'new-password'}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
                placeholder={t('Password', 'Senha')}
                className="border-edge bg-canvas w-full rounded-lg border p-3"
              />
              {!sealed && (
                <>
                  <input
                    type="password"
                    aria-label={t('Repeat the password', 'Repita a senha')}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(event) => {
                      setConfirm(event.target.value);
                    }}
                    placeholder={t('Repeat the password', 'Repita a senha')}
                    className="border-edge bg-canvas w-full rounded-lg border p-3"
                  />
                  <input
                    aria-label={t('Hint (optional)', 'Dica (opcional)')}
                    maxLength={120}
                    value={hint}
                    onChange={(event) => {
                      setHint(event.target.value);
                    }}
                    placeholder={t(
                      'Hint (optional, visible without the password)',
                      'Dica (opcional, visível sem a senha)',
                    )}
                    className="border-edge bg-canvas w-full rounded-lg border p-3"
                  />
                </>
              )}
              {password !== '' && passwordProblem !== null && (
                <p className="text-warning text-sm">{passwordProblem}</p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  tone="primary"
                  disabled={busy || passwordProblem !== null}
                  onClick={() => {
                    void run(async () => {
                      const id = sealed
                        ? await unprotectMap(summary.map.id, password, t)
                        : await protectMap(summary.map.id, password, hint, t);
                      setPassword('');
                      setConfirm('');
                      await organization.update((previous) =>
                        placeMap(previous, id, folder || null, tags),
                      );
                      setProtecting(false);
                      setSealed(!sealed);
                      setNotice(
                        sealed
                          ? t(
                              'Protection removed. The protected copy is in the Trash.',
                              'Proteção removida. A cópia protegida está na Lixeira.',
                            )
                          : t(
                              'Map protected. The original is in the Trash — empty it to delete it from this browser and from the cloud.',
                              'Mapa protegido. O original está na Lixeira — esvazie-a para apagá-lo deste navegador e da nuvem.',
                            ),
                      );
                    });
                  }}
                >
                  {sealed ? t('Remove protection', 'Remover proteção') : t('Protect', 'Proteger')}
                </Button>
                <Button
                  tone="ghost"
                  disabled={busy}
                  onClick={() => {
                    setProtecting(false);
                    setPassword('');
                    setConfirm('');
                  }}
                >
                  {t('Cancel', 'Cancelar')}
                </Button>
              </div>
            </div>
          )}
          {notice !== '' && (
            <p role="status" className="text-sm">
              {notice}
            </p>
          )}
          {(error || organization.error) && <p role="alert">{error || organization.error}</p>}
        </form>
      </Modal>
      {exported && (
        <AdvancedExport
          open
          onClose={onClose}
          document={exported}
          scene={buildScene(exported, { measurer: textMeasurer() })}
        />
      )}
    </>
  );
}
